import { z } from 'zod';

export const MarketKindSchema = z.enum([
    'binary',
    'field',
    'over_under',
    'closest',
    'free_for_all',
]);
export type MarketKind = z.infer<typeof MarketKindSchema>;

export const MarketStatusSchema = z.enum(['open', 'locked', 'resolving', 'settled', 'void']);
export type MarketStatus = z.infer<typeof MarketStatusSchema>;

export const KIND_LABELS: Record<MarketKind, string> = {
    binary: 'Yes or no',
    field: 'Who wins',
    over_under: 'Over / under',
    closest: 'Closest guess',
    free_for_all: 'Free-for-all',
};

export const OutcomeSchema = z.object({
    id: z.number().int().positive(),
    marketId: z.number().int().positive(),
    label: z.string(),
    entrantUserId: z.number().int().positive().nullable(),
    sort: z.number().int(),
    staked: z.number().int(),
    share: z.number(),
    multiplier: z.number(),
    myStake: z.number().int(),
});

export type Outcome = z.infer<typeof OutcomeSchema>;

export const MarketSchema = z.object({
    id: z.number().int().positive(),
    hangoutId: z.number().int().positive(),
    creatorId: z.number().int().positive(),
    resolverId: z.number().int().positive(),
    kind: MarketKindSchema,
    question: z.string(),
    detail: z.string().nullable(),
    // numeric arrives as a string so its exactness survives the trip.
    lineValue: z.string().nullable(),
    buyIn: z.number().int().nullable(),
    minParticipants: z.number().int().nullable(),
    joinDeadline: z.coerce.date().nullable(),
    parentMarketId: z.number().int().nullable(),
    closesAt: z.coerce.date(),
    status: MarketStatusSchema,
    resolvedOutcomeId: z.number().int().nullable(),
    resolvedValue: z.string().nullable(),
    resolvedAt: z.coerce.date().nullable(),
    outcomePostedAt: z.coerce.date().nullable(),
    createdAt: z.coerce.date(),
    pot: z.number().int(),
    bettors: z.number().int(),
    myStake: z.number().int(),
    maxStakePerMarket: z.number().int(),
    creatorHandle: z.string(),
    resolverHandle: z.string(),
    outcomes: z.array(OutcomeSchema),
});

export type Market = z.infer<typeof MarketSchema>;
export const MarketsSchema = z.array(MarketSchema);

export const SettlementSchema = z.object({
    pot: z.number().int(),
    paid: z.number().int(),
    refunded: z.boolean(),
    winners: z.number().int(),
    eliminated: z.array(z.number().int()),
});



export const MINIMUM_STAKE = 10;

export const DisputeVoteSchema = z.object({
    userId: z.number().int().positive(),
    handle: z.string(),
    agrees: z.boolean(),
});

export const DisputeSchema = z.object({
    id: z.number().int().positive(),
    marketId: z.number().int().positive(),
    raisedBy: z.number().int().positive(),
    raisedByHandle: z.string(),
    reason: z.string(),
    closedAt: z.coerce.date().nullable(),
    outcome: z.enum(['upheld', 'overturned', 'voided']).nullable(),
    createdAt: z.coerce.date(),
    votes: z.array(DisputeVoteSchema),
    agree: z.number().int(),
    disagree: z.number().int(),
});
export const DisputesSchema = z.array(DisputeSchema);
export type Dispute = z.infer<typeof DisputeSchema>;
